import fetcher, { makeFetcher } from '@sls-react/api/fetcher';
import { RailsFacetResponse } from '@sls-react/typings/services/rails/responses';
import { LockerItemPageQuery } from '@sls-react/typings/pages/lockers';
import { RailsFacetQueryOptions } from '@sls-react/typings/services/rails/facets';
import { UserPublicProfile } from '@sls-react/typings/services/iam/profiles';
import {
  APIPaginatedResponse,
  Uuid,
} from '@sls-react/typings/services/service';
import {
  RailsFeedbackRating,
  RailsFollowUser,
  RailsUser,
  RailsUserFeedback,
} from '@sls-react/typings/services/rails/user';
import { RailsCatalog } from '@sls-react/typings/services/rails/orders';
import { recaptchaHeader } from '@sls-react/api/auth';

export const fetchUserFavorites = (
  username: string,
  page: number | string = 1,
) =>
  fetcher<RailsFacetResponse>(`/v1/users/${username}/favorites`, {
    dataOnly: false,
    params: {
      page,
      sort: 'newest',
      src: 'locker',
    },
  });

export const fetchUserFollowers = makeFetcher<
  APIPaginatedResponse<RailsFollowUser[]>
>((username: string, page: number | string = 1) => [
  `/v1/users/${username}/followers`,
  {
    dataOnly: false,
    params: {
      page,
      src: 'locker',
    },
  },
]);

export const fetchUserFollowing = makeFetcher<
  APIPaginatedResponse<RailsFollowUser[]>
>((username: string, page: number | string = 1) => [
  `/v1/users/${username}/following`,
  {
    dataOnly: false,
    params: {
      page,
      src: 'locker',
    },
  },
]);

export const fetchLockerItems = makeFetcher<RailsFacetResponse>(
  ({ itemState, username, ...query }: LockerItemPageQuery) => [
    '/v2/facet_items',
    {
      dataOnly: false,
      params: {
        ...query,
        page_size: 24,
        seller: [username],
        src: 'locker',
        state: [itemState || 'available'],
      } as RailsFacetQueryOptions,
    },
  ],
);

export const fetchLockerItemsNoCache = makeFetcher<RailsFacetResponse>(
  ({ itemState, username, ...query }: LockerItemPageQuery) => [
    '/v2/facet_items',
    {
      dataOnly: false,
      params: {
        ...query,
        no_cache: 1,
        ignore_cache: 1,
        page_size: 24,
        seller: [username],
        src: 'locker',
        state: [itemState || 'available'],
      } as RailsFacetQueryOptions,
      headers: {
        pragma: 'no-cache',
        'cache-control': 'no-cache',
      },
    },
  ],
);

export const fetchLockerUserData = makeFetcher<RailsUser>((username: string) => [
  `/v1/users/${username}`,
  {
    params: {
      src: 'locker',
    },
  },
]);

export const fetchPublicProfile = uuid => fetcher<UserPublicProfile>(`/iam/v1/profile/public/${uuid}`);

export const startFollowingUser = (them: number | Uuid) =>
  fetcher(({ uuid }) => `/v1/users/${uuid}/following/${them}`, {
    method: 'put',
  });

export const stopFollowingUser = (them: number | Uuid) =>
  fetcher(({ uuid }) => `/v1/users/${uuid}/following/${them}`, {
    method: 'delete',
  });

export const fetchUserFeedback = makeFetcher<
  APIPaginatedResponse<RailsUserFeedback[]>
>(
  (
    username: string,
    rating?: RailsFeedbackRating,
    page: number | string = 1,
  ) => [
    `/v1/users/${username}/feedback`,
    {
      dataOnly: false,
      params: {
        page,
        rating: rating ? [rating] : undefined,
        src: 'locker',
      },
    },
  ],
);

export const fetchBumpsCatalog = () =>
  fetcher<RailsCatalog[]>('/v1/catalogs', {
    params: {
      type: ['bump'],
    },
  });

export const purchaseBumpsOrder = (
  catalogId: number,
  quantity: number,
  nonce: string,
  recaptchaToken: string,
) =>
  fetcher('/v1/orders', {
    method: 'post',
    body: {
      order: {
        nonce,
        quantity,
        catalog_id: catalogId,
      },
    },
    headers: {
      [recaptchaHeader]: recaptchaToken,
    },
  });

export const publishAll = () =>
  fetcher('/v1/items/publish', {
    method: 'post',
  });

export const addAllToSale = () =>
  fetcher('/v1/items/sale', {
    method: 'post',
  });
